import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { AppTheme } from '@/shared/constant/AppTheme';
import type { DocsBaseConfig } from '@/shared/constant/DocsBaseConfig';
import type { DocsIdTypeData } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { defaultAccentColor } from '@/shared/constant/themeColor';
import type { CustomCodes } from '@tarslib/types/lib/publishedProject';
import React from 'react';
import prepareDocsConfigScriptUrl from './js/prepareDocsConfigScript.js?url';

export type GlobalScriptProps = {
  docsBaseConfig: DocsBaseConfig | undefined;
  docsIdTypeData: DocsIdTypeData | undefined;
  customCodes: CustomCodes | undefined;
};

export const GlobalScript = React.memo(function GlobalScriptComponent(props: GlobalScriptProps) {
  const { docsBaseConfig, docsIdTypeData } = props;

  const id = React.useMemo(() => {
    return docsIdTypeData?.onlineId ?? 0;
  }, [docsIdTypeData]);

  const subdirectory = React.useMemo(() => {
    return docsIdTypeData?.onlineType === OnlineType.DocsSite
      ? docsIdTypeData?.subdirectory
      : undefined;
  }, [docsIdTypeData]);

  const prepareDocsConfigParams = React.useMemo(() => {
    return {
      theme: docsBaseConfig?.theme || AppTheme.System,
      themePrimarySettings: docsBaseConfig?.themePrimarySettings ?? {
        [AppTheme.Light]: {
          accentColor: defaultAccentColor,
        },
        [AppTheme.Dark]: {
          accentColor: defaultAccentColor,
        },
      },
      logoSettings: docsBaseConfig?.logoSettings ?? {
        [AppTheme.Dark]: '',
        [AppTheme.Light]: '',
      },
      id,
      subdirectory,
    };
  }, [docsBaseConfig, id, subdirectory]);
  const initialPrepareDocsConfigParams = React.useRef(prepareDocsConfigParams);
  React.useEffect(() => {
    if (initialPrepareDocsConfigParams.current !== prepareDocsConfigParams) {
      if (prepareDocsConfigParams) {
        window.__prepareDocsConfigScript(prepareDocsConfigParams);
      }
    }
  }, [prepareDocsConfigParams]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!IS_PDV && IS_RELEASE) {
        const url = (() => {
          if (IS_GLOBAL) {
            if (IS_EU) {
              return 'https://assets.eu.apidog.com/static/www/assets/js/apidog-apidoc-event-tracking.min.js';
            } else {
              return 'https://assets.apidog.com/static/www/assets/js/apidog-apidoc-event-tracking.min.js';
            }
          }

          if (!IS_GLOBAL) {
            return 'https://cdn.apifox.com/www/assets/js/apifox-apidoc-event-tracking.min.js';
          }
        })();

        if (url) {
          const script = document.createElement('script');
          script.src = url;
          script.defer = true;
          document.body.appendChild(script);
        }
      }
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (props.customCodes?.javascript) {
      try {
        const script = document.createElement('script');
        script.innerHTML = `(async () => {
          ${props.customCodes.javascript}
        })();`;
        document.body.appendChild(script);
      } catch (error) {
        console.error('load custom codes javascript failed: ', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <script src={prepareDocsConfigScriptUrl} />
      <script
        dangerouslySetInnerHTML={{
          __html: `__prepareDocsConfigScript(JSON.parse(${JSON.stringify(JSON.stringify(prepareDocsConfigParams))}));`,
        }}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.eventTracking = {
      dataLayer: []
    }
    window.eventTracking.report = function(){
        window.eventTracking.dataLayer.push(arguments);
    }`,
        }}
      />
    </>
  );
});
